<script setup lang="ts">
import {QBtn, useDialogPluginComponent} from 'quasar'
import Translator from "@/utils/common/Translator.ts";
import {PropType} from "vue";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import TooltipIcon from "@/assets/editViewIcons/TooltipIcon.vue";
import FieldToFocusUtils from "@/utils/common/FieldToFocusUtils.ts";
import MetaData from "@/utils/common/MetaData.ts";
import TooltipUtils from "@/utils/common/TooltipUtils.ts";
import {TooltipContext} from "@/utils/UIConstants/enums.ts";
import type ListPresenter from "@/utils/common/ListPresenter.ts";
import type SearcherViewPresenter from "@/utils/common/SearcherViewPresenter.ts";
import {GridApi} from "ag-grid-enterprise";
import NamaIcon from "@/components/icons/NamaIcon.vue";

const props = defineProps({
  title: {type: String, required: true},
  presenter: {type: Object as PropType<EditPresenter | ListPresenter | SearcherViewPresenter>, required: true},
  context: {type: String as PropType<TooltipContext>, default: TooltipContext.EditView},
});
const {compactMode} = storeToRefs(useNamaFlagsStore());
const {dialogRef, onDialogCancel} = useDialogPluginComponent();
const fieldIds = TooltipUtils.fetchTooltipFieldIds(props.presenter?.entityType, props.context);

async function onClick(id: string) {
  if (id) {
    if (props.context === TooltipContext.EditView)
      await runEditViewTooltip(id);
    else
      await runListTooltip(id);
  }
  onDialogCancel();
}

async function runEditViewTooltip(id: string) {
  let presenter = props.presenter as EditPresenter;
  let row = -1;
  let gridId = MetaData.getFieldParentDetail0(presenter.fetchMetadata(), id, false);
  if (gridId)
    row = presenter.fetchGridSelectedLine(gridId) ?? 0;
  await presenter.fetchDispatcher().displayTooltip(id, gridId ?? id, row, false);
  await FieldToFocusUtils.focusToField(new FieldToFocusUtils(presenter), id, row);
}

async function runListTooltip(id: string) {
  let presenter = props.presenter as ListPresenter | SearcherViewPresenter;
  let api = presenter.gridApi.value;
  if (!api)
    return;
  await TooltipUtils.runListTooltipForField(api, presenter.entityType ?? '', props.context, presenter.fetchMetadata(), id, pickTargetRow(api));
}

function pickTargetRow(api: GridApi): number {
  let focusedRow = api.getFocusedCell()?.rowIndex;
  if (focusedRow != null && focusedRow >= 0)
    return focusedRow;
  let selectedRow = api.getSelectedNodes()[0]?.rowIndex;
  if (selectedRow != null && selectedRow >= 0)
    return selectedRow;
  return 0;
}

function onCancelClick() {
  onDialogCancel();
}
</script>

<template>

  <q-dialog ref="dialogRef" @hide="onCancelClick">
    <q-card
      class="tw-w-[25%] mobile:tw-w-[80%] tw-h-fit tw-shadow-none dark:tw-text-white dark:tw-bg-namaBackground-deep-dark tw-rounded-2xl tw-bg-namaBackground-deep-light ">
      <q-bar v-if="title" class="tw-text-xs tw-text-namaFontColor-input-light tw-bg-[#CED4DA] dark:tw-bg-[#212529] dark:tw-text-white tw-h-10">
        <div>{{ Translator.translateId('tooltips') }}</div>
        <q-space/>
        <NamaIcon default-icon="close" @click="onCancelClick" :title="Translator.translateId('close')" class="tw-size-5 tw-cursor-pointer"/>
      </q-bar>
      <div class="tw-w-full tw-h-full tw-p-3 tw-place-self-center">
        <q-card-section class="tw-flex tw-flex-wrap tw-justify-center tw-w-full tw-h-fit tw-max-h-[60vh] tw-gap-5 tw-overflow-y-auto">
          <template v-for="(id, index) in fieldIds" :key="index">
            <q-btn
              outline no-caps
              :title="Translator.translateId(id,presenter.entityType)"
              @click="onClick(id)"
              :dense="compactMode"
              class="tw-w-[45%] mobile:tw-w-full tw-text-primary-light dark:tw-text-primary-dark tw-text-base tw-font-normal tw-rounded-md tw-p-2">
              <TooltipIcon class="tw-size-5 compact:tw-size-[18px] ultra:tw-size-[15px] tw-mr-2"/>
              <div class="tw-text-namaFontColor-input-light dark:tw-text-white">{{ Translator.translateId(id, presenter.entityType) }}</div>
            </q-btn>
          </template>
        </q-card-section>
      </div>
    </q-card>
  </q-dialog>
</template>

<style scoped>

</style>
